/**
 * NEW_PLANS_FLAG
 *
 * Centralized feature flag for the new plans UI (PlansCards, UpgradeFeatureModal,
 * UpgradeWindow, UpgradePlanSuccessModal, new account-creation plan/payment pages).
 *
 * Gate: both `beta_tester` AND `beta_tester_internal` user flags must be true.
 *
 * To launch to all users:
 *   1. Search for "isNewPlans" / "isNewPlansEnabled" across the codebase
 *   2. Remove all conditional branches, keeping only the "new plans" code
 *   3. Delete this file
 *   4. Remove legacy account-creation pages (src/views/Auth/AccountCreation/legacy/)
 */

import { computed } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { INVENTORY_PILOT_EMAILS } from '@/constants/pilots'

// On localhost we treat every gated feature as enabled, so devs can work on
// flagged surfaces (Orders, the upgraded Sale Pick List, etc.) without the BE
// flag set on their account. Real environments still honour the flag.
export const isLocalhost = () =>
  typeof window !== 'undefined' && window.location?.hostname === 'localhost'

export const useNewPlans = () => {
  const userStore = useUserStore()
  return computed(() => !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal))
}

export const isNewPlansEnabled = () => {
  const userStore = useUserStore()
  return !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal)
}

/**
 * Inventory redesign — the whole rollout, pilot only.
 * Gate: the email allowlist in `constants/pilots.js` (localhost always on).
 *
 * The predicate itself lives on `UserStore.isInventoryPilot` because
 * `isNewDesign` is AND-ed with it, and this file imports the store (defining it
 * here would be a circular import). This composable is the ergonomic wrapper
 * for components.
 *
 * Gates BOTH halves of the feature:
 *   - the design itself, via `isNewDesign` → every grid / cell / panel already
 *     branching on it, no per-consumer flag check needed;
 *   - the opt-in surfaces that advertise it — the header switch + its confirm
 *     modal, the sidebar promo, the welcome modals, the first-run tour and the
 *     user-menu row. Those are checked explicitly so a classic user never even
 *     sees the invitation.
 */
export const useInventoryPilot = () => {
  const userStore = useUserStore()
  return computed(() => userStore.isInventoryPilot)
}

/**
 * Non-production = localhost, the dev API, or a dev/staging host.
 *
 * Deliberately an ALLOWLIST of dev markers rather than a denylist of prod ones:
 * an unrecognised host counts as production, so a new prod domain can never
 * accidentally light up an unreleased feature. Checks the API host as well as
 * the browser host, because the dev site is served from a different domain
 * (`dev.cdp2.torpedo.io`) than the dev API (`cdp-dev.collx.app`).
 */
export const isNonProduction = () => {
  if (isLocalhost()) return true

  const apiHost = String(import.meta.env.VITE_API_HOST || '')
  const browserHost = typeof window !== 'undefined' ? (window.location?.hostname || '') : ''

  return /(^|[.\-])dev([.\-]|$)|cdp-dev|torpedo\.io|staging/i.test(`${apiHost} ${browserHost}`)
}

/**
 * Title character count ("45/80") + its over-limit red state, in the grid
 * editor, the Inspector / Inventory-details Custom Title and the eBay listing
 * title. Dev-only while it's being tried out; the pre-existing over-80 warning
 * in the grid is untouched for everyone.
 */
export const isTitleCharacterCountEnabled = () => isNonProduction()

export const useTitleCharacterCount = () => computed(() => isTitleCharacterCountEnabled())

/**
 * ── Pilot allowlists ────────────────────────────────────────────────────────
 *
 * Deliberately email lists, not BE flags: these are a handful of reviewers on
 * production, granted or revoked without a backend change. Swap any of them for
 * a `user.flags` gate when it graduates past a pilot.
 *
 * Both sides of the comparison are lower-cased, so a list can be written the way
 * the person writes their own address.
 */
const isPilotUser = (allowlist) => {
  const email = useUserStore().user?.email?.trim().toLowerCase()

  // Localhost sees every gated feature (see `isLocalhost` above).
  return isLocalhost() || (!!email && allowlist.some(entry => entry.trim().toLowerCase() === email))
}

/**
 * Inventory hits inside GLOBAL SEARCH (CDP-2508).
 *
 * The `/search` response can carry an optional `inventory` block (the user's own
 * matching cards), which turns a Card Database row's "Add Card" into
 * "View in Inventory" / "View in Batch". Everyone not on this list keeps the
 * previous behaviour exactly — the store drops the block, so there is nothing
 * for the UI to render and no branch to hit.
 *
 * ⚠️ This flag ALSO picks the API host: pilot users hit the main API for
 * `/search` (`apiClient()`), everyone else the batch processor
 * (`apiClient(true)`) — see `SearchStore.searchDatabaseMatches`. So a user on
 * this list gets a different backend, not just extra UI.
 */
export const isSearchInventoryEnabled = () => isPilotUser(INVENTORY_PILOT_EMAILS)

export const useSearchInventory = () => computed(() => isSearchInventoryEnabled())

/*
 * Launched, flags removed (2026-09-02):
 *   title abbreviations — the "Use abbreviations for card attributes and teams"
 *                   checkbox in the title-template modal. On for everyone; the
 *                   setting itself is `title_team_abbreviations_enabled` on the
 *                   user and is unchanged.
 *   shopify location — the default inventory location section + its
 *                   `/users/me/shopify/locations` fetch. On for everyone; the
 *                   section still hides itself when the store has no locations
 *                   to choose between.
 *
 * Launched, flags removed (2026-08-24):
 *   `bulk-delist` — Bulk End / Relist. Now on for everyone.
 *   `csv-import`  — Inventory CSV import. Now on for everyone; the PLAN
 *                   entitlement (`csv-inventory-import`) still gates it inside
 *                   `InventoryCsvImportModal` and is unrelated to this file.
 */
